import { customizer } from '@sas/design-tokenizer';
import PRODUCT_TOKENS_JSON_NEW from '@sas/new-product-tokens/processed-tokens.json';
import { getLogger } from '@sas/nova-core/logging';
import PRODUCT_TOKENS_JSON from '@sas/product-tokens/processed-tokens.json';
import { hsl, parseToHsl } from 'polished';
import { isSystemReportTheme } from './reportThemes';
import { ApplicationThemeParametersMap, defaultThemes, ThemeIdMap } from './themeParameters';
const logger = getLogger('themeOverrides');
const colorMappings = [{
  token: 'colorNeutral',
  themeVar: '@neutralColorThemeDesigner'
}, {
  token: 'colorPrimary',
  themeVar: '@primaryColorThemeDesigner'
}, {
  token: 'colorSecondary',
  themeVar: '@secondaryColorThemeDesigner'
}, {
  token: 'colorAlert',
  themeVar: '@alertColor'
}, {
  token: 'colorWarning',
  themeVar: '@warningColor'
}, {
  token: 'colorSuccess',
  themeVar: '@successColor'
}, {
  token: 'colorInfo',
  themeVar: '@informationColor'
}, {
  token: 'colorStatusNeutral',
  themeVar: '@neutralColorThemeDesigner'
}, {
  token: 'colorGraph1',
  themeVar: '@GraphPaletteColor1'
}, {
  token: 'colorGraph2',
  themeVar: '@GraphPaletteColor2'
}, {
  token: 'colorGraph3',
  themeVar: '@GraphPaletteColor3'
}, {
  token: 'colorGraph4',
  themeVar: '@GraphPaletteColor4'
}, {
  token: 'colorGraph5',
  themeVar: '@GraphPaletteColor5'
}, {
  token: 'colorGraph6',
  themeVar: '@GraphPaletteColor6'
}, {
  token: 'colorGraph7',
  themeVar: '@GraphPaletteColor7'
}, {
  token: 'colorGraph8',
  themeVar: '@GraphPaletteColor8'
}, {
  token: 'colorKPI1',
  themeVar: '@kpiPaletteRange1'
}, {
  token: 'colorKPI3',
  themeVar: '@kpiPaletteRange3'
}, {
  token: 'colorKPI5',
  themeVar: '@kpiPaletteRange5'
},
// Legacy tokens
{
  token: 'primary',
  themeVar: '@primaryColorThemeDesigner'
}, {
  token: 'secondary',
  themeVar: '@secondaryColorThemeDesigner'
}, {
  token: 'neutral',
  themeVar: '@neutralColorThemeDesigner'
}, {
  token: 'alert',
  themeVar: '@alertColor'
}, {
  token: 'warning',
  themeVar: '@warningColor'
}, {
  token: 'success',
  themeVar: '@successColor'
}, {
  token: 'information',
  themeVar: '@informationColor'
}];
const overrideMappings = [{
  token: 'fontFamily',
  themeVar: '@fontFamilyOverride',
  // TD saves the font family using a LESS escape character (`~','`) so we need to remove it
  modify: val => val.replace(/['"\\~,]+/g, '')
}, {
  token: 'fontSizeCore',
  themeVar: '@baseLineFontSizeCore'
}, {
  token: 'fontSizeBase',
  themeVar: '@baseLineFontSize'
}, {
  token: 'bannerBg',
  themeVar: '@bannerBackgroundColor'
}, {
  token: 'bannerFg',
  themeVar: '@bannerTextColor'
},
// TD saves images with escaped quotations so we need to remove them
{
  token: 'bannerImg',
  themeVar: '@bannerImageId',
  modify: val => val.replace(/"/g, '')
}, {
  token: 'fontSizeBase',
  themeVar: '@baseLineFontSize'
}, {
  token: 'splashFg',
  themeVar: '@splashScreenAnimationColor'
}, {
  token: 'splashBg',
  themeVar: '@splashScreenBackgroundColor'
},
// TD saves images with escaped quotations so we need to remove them
{
  token: 'splashImg',
  themeVar: '@splashImageId',
  modify: val => val.replace(/"/g, '')
}, {
  token: 'transitionFg',
  themeVar: '@transitionScreenAnimationColor'
}, {
  token: 'transitionBg',
  themeVar: '@transitionScreenBackgroundColor'
}, {
  token: 'tableBorderColor',
  themeVar: '@tableBorderColor'
}, {
  token: 'tableCellBorderColor',
  themeVar: '@tableCellBorderColor'
}, {
  token: 'tableHeaderBackground',
  themeVar: '@tableHeaderBackground'
}, {
  token: 'tableHeaderBorderRightColor',
  themeVar: '@tableHeaderBorderRightColor'
}, {
  token: 'tableHeaderLabelColor',
  themeVar: '@tableHeaderLabelColor'
}, {
  token: 'tableRowBackgroundColorEven',
  themeVar: '@tableRowBackgroundColorEven'
}, {
  token: 'tableRowBackgroundColorOdd',
  themeVar: '@tableRowBackgroundColorOdd'
}, {
  token: 'tableTextColor',
  themeVar: '@tableTextColor'
}, {
  token: 'tableTotalsBackgroundColor',
  themeVar: '@tableTotalsBackgroundColor'
}, {
  token: 'tableTotalsTextColor',
  themeVar: '@tableTotalsTextColor'
},
// TD saves images with escaped quotations so we need to remove them
{
  token: 'transitionImg',
  themeVar: '@transitionImageId',
  modify: val => val.replace(/"/g, '')
}];

/**
 * Convert a response from the theme's service to `ThemeParameterOverrides`.
 *
 * The `ThemeParameterOverrides` object returned from this function should be
 * passed into either `resolveCustomApplicationTheme` for application themes or
 * `resolveReportThemeOverrides` for report themes to generate a `ThemeParameters`
 * object.
 */
export const convertThemeServiceResponseToThemeParameterOverrides = response => {
  response = response ?? {};
  let themeId = response.baseThemeId;
  let baseReportThemeName = undefined;
  if (response.contentType === 'application') {
    if (!themeId || !isSystemApplicationThemeId(themeId)) {
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      logger.warn(`Unsupported application 'baseThemeId' provided ('${themeId}'). Defaulting to 'sas_corporate'.`);
      themeId = 'sas_corporate';
    }
  } else if (response.contentType === 'report') {
    if (!themeId || !isSystemReportTheme(themeId)) {
      // eslint-disable-next-line @sas/i18n/jsx-to-localize
      logger.warn(`Unsupported report 'baseThemeId' provided ('${themeId}'). Defaulting to 'light'.`);
      themeId = 'light';
    }
    baseReportThemeName = themeId;
  } else {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.warn(`Unrecognized value for 'contentType' provided ('${response.contentType}').`);
    if (themeId && isSystemApplicationThemeId(themeId)) {
      // assume application theme
    } else if (themeId && isSystemReportTheme(themeId)) {
      // assume report theme and set baseReportThemeName
      baseReportThemeName = themeId;
    } else {
      themeId = 'sas_corporate';
    }
  }
  const baseThemeName = ThemeIdMap[themeId].baseThemeName;
  const colors = {};
  for (const {
    themeVar,
    token
  } of colorMappings) {
    const value = response.styleData?.[themeVar];
    if (value) {
      colors[token] = value;
    }
  }
  const overrides = {};
  for (const {
    themeVar,
    token,
    modify
  } of overrideMappings) {
    const value = response.styleData?.[themeVar];
    if (value) {
      overrides[token] = modify ? modify(value) : value;
    }
  }
  return {
    baseThemeName,
    ...(baseReportThemeName ? {
      baseReportThemeName
    } : {}),
    colors,
    overrides
  };
};

/**
 * Determine if a theme name is a base theme name.
 */
export const isBaseThemeName = themeName => {
  return defaultThemes.hasOwnProperty(themeName);
};

/**
 * Determine if a theme id is a base application theme id.
 */
export const isSystemApplicationThemeId = themeId => {
  return ApplicationThemeParametersMap.hasOwnProperty(themeId);
};

/**
 * Resolves a theme service response into a format compatible with the `ThemeProvider`.
 */
export const resolveCustomApplicationTheme = (themeServiceResponse, themeOverride, isNewTokenValueFlagEnabled) => {
  const customTheme = convertThemeServiceResponseToThemeParameterOverrides({
    ...themeServiceResponse,
    baseThemeId: themeOverride ?? themeServiceResponse.baseThemeId
  });
  let baseThemeName = customTheme.baseThemeName;
  if (!isBaseThemeName(baseThemeName)) {
    // eslint-disable-next-line @sas/i18n/jsx-to-localize
    logger.warn(`Unsupported 'baseThemeName' provided ('${baseThemeName}'). Defaulting to 'light'.`);
    baseThemeName = 'light';
  }
  const baseTheme = defaultThemes[baseThemeName];
  const colorOverrides = deriveNovaTokens(customTheme);

  // Feature flag for new token values
  const featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues = isNewTokenValueFlagEnabled;
  const productTokens = featureFlag_NOVACOMMONS_22288_ThemeModernization_NewTokenValues ? PRODUCT_TOKENS_JSON_NEW : PRODUCT_TOKENS_JSON;
  const customizedTokens = customizer(productTokens, {
    'color-neutral': themeServiceResponse.styleData['@neutralColorThemeDesigner'],
    'color-primary': themeServiceResponse.styleData['@primaryColorThemeDesigner'],
    'color-secondary': themeServiceResponse.styleData['@secondaryColorThemeDesigner'],
    'color-status-alert': themeServiceResponse.styleData['@alertColor'],
    'color-status-warning': themeServiceResponse.styleData['@warningColor'],
    'color-status-info': themeServiceResponse.styleData['@informationColor'],
    'color-status-success': themeServiceResponse.styleData['@successColor'],
    'color-dataviz-graph1': themeServiceResponse.styleData['@GraphPaletteColor1'],
    'color-dataviz-graph2': themeServiceResponse.styleData['@GraphPaletteColor2'],
    'color-dataviz-graph3': themeServiceResponse.styleData['@GraphPaletteColor3'],
    'color-dataviz-graph4': themeServiceResponse.styleData['@GraphPaletteColor4'],
    'color-dataviz-graph5': themeServiceResponse.styleData['@GraphPaletteColor5'],
    'color-dataviz-graph6': themeServiceResponse.styleData['@GraphPaletteColor6'],
    'color-dataviz-graph7': themeServiceResponse.styleData['@GraphPaletteColor7'],
    'color-dataviz-graph8': themeServiceResponse.styleData['@GraphPaletteColor8'],
    'color-dataviz-kpi1': themeServiceResponse.styleData['@kpiPaletteRange1'],
    'color-dataviz-kpi3': themeServiceResponse.styleData['@kpiPaletteRange3'],
    'color-dataviz-kpi5': themeServiceResponse.styleData['@kpiPaletteRange5'],
    'brand-color-blue': themeServiceResponse.styleData['@primaryColorThemeDesigner'],
    'brand-color-grey': themeServiceResponse.styleData['@neutralColorThemeDesigner'],
    'brand-color-cranberry': themeServiceResponse.styleData['@secondaryColorThemeDesigner'],
    'brand-color-info': themeServiceResponse.styleData['@informationColor'],
    'brand-color-warning': themeServiceResponse.styleData['@warningColor'],
    'brand-color-alert': themeServiceResponse.styleData['@alertColor'],
    'brand-color-success': themeServiceResponse.styleData['@successColor']
    // @fontFamilyOverride is handled in react.ts as it needs to be locale aware
  });

  const tokensExperimental = {};
  (customizedTokens.find(t => t.name === baseThemeName)?.tokens ?? []).forEach(({
    name,
    value
  }) => {
    // font-family needs to be locale aware so the overrides are being done in react.ts
    if (name === 'font-family-primary' || name === 'font-family-secondary') return;
    tokensExperimental[name] = value;
  });
  return {
    ...baseTheme,
    baseThemeName,
    tokensExperimental,
    tokens: tokensExperimental,
    colors: {
      ...baseTheme.colors,
      ...colorOverrides
    },
    overrides: {
      ...baseTheme.overrides,
      ...customTheme.overrides
    },
    enableNovaTokens: themeServiceResponse.contentType !== 'report'
  };
};

/**
 * Derive Nova color palette.
 */
export function deriveNovaTokens(theme) {
  let colorNeutral = theme.colors?.colorNeutral;
  let colorPrimary = theme.colors?.colorPrimary;
  let defaultTheme;
  let colorUiLight;
  let colorUiMedium;
  let colorUiDark;
  let colorUiBorder;
  let colorUiPop;
  let colorTextDefault;
  let colorTextLabel;
  let colorTextInverse;
  const colorTextPop = hsl(0, 1, 1);
  switch (theme.baseThemeName) {
    case 'high-contrast':
      defaultTheme = defaultThemes['high-contrast'];
      colorNeutral = colorNeutral ?? defaultTheme.colors.colorNeutral;
      colorPrimary = colorPrimary ?? defaultTheme.colors.colorPrimary;
      colorUiLight = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0);
      colorUiMedium = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.17);
      colorUiDark = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0);
      colorUiBorder = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.83);
      colorUiPop = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.12);
      colorTextDefault = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 1);
      colorTextLabel = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 1);
      colorTextInverse = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0);
      break;
    case 'dark':
      defaultTheme = defaultThemes.dark;
      colorNeutral = colorNeutral ?? defaultTheme.colors.colorNeutral;
      colorPrimary = colorPrimary ?? defaultTheme.colors.colorPrimary;
      colorUiLight = hsl(parseToHsl(colorPrimary).hue, parseToHsl(colorPrimary).saturation, 0.08);
      colorUiMedium = hsl(parseToHsl(colorPrimary).hue, parseToHsl(colorPrimary).saturation, 0.15);
      colorUiDark = hsl(parseToHsl(colorPrimary).hue, parseToHsl(colorPrimary).saturation, 0.12);
      colorUiBorder = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.26);
      colorUiPop = hsl(parseToHsl(colorPrimary).hue, parseToHsl(colorPrimary).saturation, 0.15);
      colorTextDefault = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 1);
      colorTextLabel = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.64);
      colorTextInverse = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0);
      break;
    default:
      defaultTheme = defaultThemes.light;
      colorNeutral = colorNeutral ?? defaultTheme.colors.colorNeutral;
      colorPrimary = colorPrimary ?? defaultTheme.colors.colorPrimary;
      colorUiLight = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 1);
      colorUiMedium = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.98);
      colorUiDark = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.95);
      colorUiBorder = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.83);
      colorUiPop = colorPrimary;
      colorTextDefault = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0);
      colorTextLabel = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 0.46);
      colorTextInverse = hsl(parseToHsl(colorNeutral).hue, parseToHsl(colorNeutral).saturation, 1);
  }
  return {
    colorNeutral,
    colorPrimary,
    colorSecondary: theme.colors?.colorSecondary ?? defaultTheme.colors.colorSecondary,
    colorAlert: theme.colors?.colorAlert ?? defaultTheme.colors.colorAlert,
    colorWarning: theme.colors?.colorWarning ?? defaultTheme.colors.colorWarning,
    colorSuccess: theme.colors?.colorSuccess ?? defaultTheme.colors.colorSuccess,
    colorInfo: theme.colors?.colorInfo ?? defaultTheme.colors.colorInfo,
    colorStatusNeutral: theme.colors?.colorStatusNeutral ?? defaultTheme.colors.colorStatusNeutral,
    colorUiLight,
    colorUiMedium,
    colorUiDark,
    colorUiBorder,
    colorUiPop,
    colorTextDefault,
    colorTextLabel,
    colorTextInverse,
    colorTextPrimary: theme.colors?.colorPrimary ?? defaultTheme.colors.colorTextPrimary,
    colorTextSecondary: theme.colors?.colorSecondary ?? defaultTheme.colors.colorTextSecondary,
    colorTextPop,
    colorGraph1: theme.colors?.colorGraph1 ?? defaultTheme.colors.colorGraph1,
    colorGraph2: theme.colors?.colorGraph2 ?? defaultTheme.colors.colorGraph2,
    colorGraph3: theme.colors?.colorGraph3 ?? defaultTheme.colors.colorGraph3,
    colorGraph4: theme.colors?.colorGraph4 ?? defaultTheme.colors.colorGraph4,
    colorGraph5: theme.colors?.colorGraph5 ?? defaultTheme.colors.colorGraph5,
    colorGraph6: theme.colors?.colorGraph6 ?? defaultTheme.colors.colorGraph6,
    colorGraph7: theme.colors?.colorGraph7 ?? defaultTheme.colors.colorGraph7,
    colorGraph8: theme.colors?.colorGraph8 ?? defaultTheme.colors.colorGraph8,
    colorKPI1: theme.colors?.colorKPI1 ?? defaultTheme.colors.colorKPI1,
    colorKPI3: theme.colors?.colorKPI3 ?? defaultTheme.colors.colorKPI3,
    colorKPI5: theme.colors?.colorKPI5 ?? defaultTheme.colors.colorKPI5
  };
}
//# sourceMappingURL=themeOverrides.js.map